import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d475tq3hnd8fvojd2ehg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: 'ri-instagram-line', name: 'Instagram', url: 'https://instagram.com/anietudoh' },
    { icon: 'ri-linkedin-line', name: 'LinkedIn', url: 'https://linkedin.com/in/anietudoh' },
    { icon: 'ri-dribbble-line', name: 'Dribbble', url: 'https://dribbble.com/anietudoh' },
    { icon: 'ri-behance-line', name: 'Behance', url: 'https://behance.net/anietudoh' }
  ];

  return (
    <section id="contact" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h4 className="text-yellow-400 font-semibold text-lg mb-4">
            <a href="#contact" className="hover:text-yellow-300 transition-colors">Get In Touch</a>
          </h4>
          <h3 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Let's Create Something <span className="text-yellow-400">Amazing</span>
          </h3>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            Ready to bring your vision to life? I'd love to hear about your project and discuss how we can work together to create exceptional designs.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="space-y-8">
            <div>
              <h4 className="text-2xl font-bold text-white mb-6">Send Me a Message</h4>
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div>
                  <label htmlFor="name" className="block text-gray-300 font-medium mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-yellow-400 focus:outline-none transition-colors text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-gray-300 font-medium mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-yellow-400 focus:outline-none transition-colors text-sm"
                    placeholder="Enter your email address"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-gray-300 font-medium mb-2">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-yellow-400 focus:outline-none transition-colors resize-none text-sm"
                    placeholder="Tell me about your project, timeline, and budget..."
                  ></textarea>
                  <div className="text-right text-gray-400 text-xs mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending Message...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send Message
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg">
                    <i className="ri-check-line mr-2"></i>
                    Thank you! Your message has been sent successfully. I'll get back to you within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
                    <i className="ri-error-warning-line mr-2"></i>
                    Sorry, there was an error sending your message. Please try again or contact me directly.
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h4 className="text-2xl font-bold text-white mb-6">Let's Connect</h4>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-400/20 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-yellow-400 text-xl"></i>
                  </div>
                  <div>
                    <h5 className="text-white font-semibold mb-1">Email</h5>
                    <p className="text-gray-300">anietieu6@gmail.com</p>
                    <p className="text-gray-400 text-sm">I respond within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-400/20 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-yellow-400 text-xl"></i>
                  </div>
                  <div>
                    <h5 className="text-white font-semibold mb-1">Phone</h5>
                    <p className="text-gray-300">+234 903 670 1928</p>
                    <p className="text-gray-400 text-sm">Available Mon-Fri, 9AM-6PM EST</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-yellow-400/20 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-yellow-400 text-xl"></i>
                  </div>
                  <div>
                    <h5 className="text-white font-semibold mb-1">Location</h5>
                    <p className="text-gray-300">Online</p>
                    <p className="text-gray-400 text-sm">Remote collaboration specialist</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h5 className="text-white font-semibold mb-4">Follow My Work</h5>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-yellow-400 text-gray-400 hover:text-black rounded-lg transition-all duration-300 hover:transform hover:scale-110 cursor-pointer"
                    title={social.name}
                  >
                    <i className={`${social.icon} text-xl`}></i>
                  </a>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-black/50 p-6 rounded-xl border border-gray-800">
              <h5 className="text-white font-semibold text-lg mb-2">Ready to Start Your Project?</h5>
              <p className="text-gray-400 text-sm mb-4">
                Let's discuss your vision and create something extraordinary together.
              </p>
              <button 
                onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                className="bg-yellow-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-yellow-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Schedule a Call
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
